/* eslint-disable react/no-this-in-sfc */
/* eslint-disable no-template-curly-in-string */
import React, { FC, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useAppDispatch } from "src/store";
import { useSelector } from "react-redux";
import type { RootState } from "@redux/rootReducer";
import { fetchDashboardChart, DashboardChartType } from "@redux/dashboardSlice";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Spin } from "antd";
import USCurrencyFormmater from "@utils/USCurrencyFormmater";
import { OrderStatusChartItem, OrderStatusChart } from "@api/dashboardApi";
import PageTitle from "@component/PageTitle";
import ChartFilter from "../ChartFilter";
import {
  getPointStart,
  getPointInterval,
  getTimeFormat,
  getTooltipTitle,
  getFilledTimeData,
  getTimeRangeText,
} from "../utils";

const Wrapper = styled.div`
  padding: 10px 25px;
`;
const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
  padding: 30px;
  min-height: 700px;
`;

const ReturnProductChart: FC = () => {
  const dispatch = useAppDispatch();
  const { isFetchingChart, chartResult, chartParams } = useSelector((state: RootState) => state.dashboard);

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>();

  useEffect(() => {
    const { startDate, endDate, cStartDate, cEndDate, timeUnit } = chartParams;
    const pointStart = getPointStart(startDate, timeUnit);
    const pointInterval = getPointInterval(timeUnit);
    const filledCurrentTimeMap = getFilledTimeData<OrderStatusChartItem>(
      startDate,
      endDate,
      (chartResult as OrderStatusChart).currentData,
      timeUnit,
    );
    const filledCompareTimeMap = getFilledTimeData<OrderStatusChartItem>(
      cStartDate,
      cEndDate,
      (chartResult as OrderStatusChart).compareData || [],
      timeUnit,
    );
    const currentRate: (number | null)[] = [];
    const currentAvgAmount: (number | null)[] = [];
    const currentCount: (number | null)[] = [];
    const compareRate: (number | null)[] = [];
    const compareAvgAmount: (number | null)[] = [];
    const compareCount: (number | null)[] = [];
    const currentTimes = Array.from(filledCurrentTimeMap.keys());
    const compareTimes = Array.from(filledCompareTimeMap.keys());
    const currentFilledTimeData = Array.from(filledCurrentTimeMap.values());
    const compareFilledTimeData = Array.from(filledCompareTimeMap.values());

    currentFilledTimeData.forEach((data) => {
      const { rate = null, avgAmount = null, count = null } = data;
      currentRate.push(rate);
      currentAvgAmount.push(avgAmount);
      currentCount.push(count);
    });
    compareFilledTimeData.forEach((data) => {
      const { rate = null, avgAmount = null, count = null } = data;
      compareRate.push(rate);
      compareAvgAmount.push(avgAmount);
      compareCount.push(count);
    });

    const currentTimeRangeText = getTimeRangeText(startDate, endDate, timeUnit);
    const compareTimeRangeText = getTimeRangeText(cStartDate, cEndDate, timeUnit);

    const series: Highcharts.SeriesOptionsType[] = [
      {
        name: currentTimeRangeText,
        data: currentRate,
        type: "line",
        color: "#5C6AC4",
        stack: "current",
        pointStart,
      },
    ];

    if (cStartDate && cEndDate) {
      series.push({
        name: compareTimeRangeText,
        data: compareRate,
        type: "line",
        color: "#1890FF",
        stack: "compare",
        pointStart,
      });
    }

    setChartOptions({
      title: {
        text: "",
      },
      chart: {
        type: "line",
      },
      xAxis: {
        type: "datetime",
        labels: {
          formatter() {
            const timeFormat = getTimeFormat(timeUnit);
            const formattedText = Highcharts.dateFormat(timeFormat, this.value as number);
            return formattedText;
          },
          rotation: -45,
        },
      },
      yAxis: {
        title: {
          text: "",
        },
        labels: {
          formatter() {
            const formatValue = Math.round((this.value as number) * 100);
            return `${formatValue}%`;
          },
        },
      },
      tooltip: {
        formatter() {
          const pointIndex = this.point.index;
          const stackName = this.point.series.userOptions.stack;
          const title =
            stackName === "current"
              ? getTooltipTitle(startDate, endDate, currentTimes[pointIndex], timeUnit)
              : getTooltipTitle(cStartDate, cEndDate, compareTimes[pointIndex], timeUnit);
          const rateData = stackName === "current" ? currentRate : compareRate;
          const countData = stackName === "current" ? currentCount : compareCount;
          const avgAmountData = stackName === "current" ? currentAvgAmount : compareAvgAmount;

          const cancelRatePercentage = Math.round((rateData[pointIndex] as number) * 1000) / 10;
          const cancelCount = countData[pointIndex];
          const cancelAvgAmount = USCurrencyFormmater.format(avgAmountData[pointIndex] as number);

          return (
            `${title}<br/>` +
            `退貨率 ${cancelRatePercentage}%<br/>` +
            `退貨子訂單數 ${cancelCount}<br/>` +
            `平均退貨金額 ${cancelAvgAmount}<br/>`
          );
        },
        backgroundColor: "#5D5954",
        borderColor: "#5D5954",
        style: {
          color: "#FFFFFF",
          fontSize: "12px",
          lineHeight: "22px",
        },
        // useHTML: true,
      },
      legend: {
        align: "right",
        verticalAlign: "middle",
        layout: "vertical",
      },
      plotOptions: {
        series: {
          pointStart,
          pointInterval,
        },
      },
      series,
    });
  }, [chartResult, chartParams]);

  const fetchOrderReturnChart = useCallback(() => {
    dispatch(fetchDashboardChart(DashboardChartType.ORDER_RETURN));
  }, [dispatch]);

  return (
    <Spin spinning={isFetchingChart}>
      <Wrapper>
        <ChartWrapper>
          <PageTitle title="Dashboard - 退貨" />
          <ChartFilter title="退貨" fetchAction={fetchOrderReturnChart} />
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: 500, marginBottom: 40 } }}
          />
        </ChartWrapper>
      </Wrapper>
    </Spin>
  );
};

export default ReturnProductChart;
